import React, { useState } from "react";
import { Icon } from "../../icons.jsx";
import { Card, Button, Badge, Segmented, Modal, ClasificacionBadge } from "../components.jsx";
import { useStore } from "../store.jsx";
import { go } from "../rutas.js";
import { clasificacionDePuntos } from "../data.js";
import {
  CATEGORIAS_MINISTERIO, SUBGRUPOS_MINISTERIO, EJES_MINISTERIO, textosDeSubfactor,
  subfactoresAplicables, nivelDeRespuesta, puntosDeSubfactor, puntosPorCategoria,
  progresoMinisterio, conDosDecimales,
} from "../ministerio.js";

/* ============================================================
   Talention — Valoración de un puesto con el modelo Ministerio
   Una pestaña por categoría; cada subfactor se responde con un
   nivel (0 = no relevante) o, en las matrices, con los dos ejes.
   En pantalla los puntos van redondeados; con dos decimales solo
   por subfactor y en el informe.
   ============================================================ */

const NO_RELEVANTE = "0. La utilización de este factor no es relevante para este puesto de trabajo";

/* Textos de un subfactor. Los «Otros» los escribe la empresa: su nombre,
   su justificación y sus 5 niveles, con el 0 oficial delante. */
function textosDe(sf, otros) {
  if (!sf.otros) return textosDeSubfactor(sf.codigo);
  const otro = otros[sf.codigo];
  return {
    nombre: `${sf.codigo}) ${otro.nombre}`,
    definicion: otro.justificacion,
    niveles: [NO_RELEVANTE, ...otro.niveles.map((t, i) => `${i + 1}. ${t}`)],
  };
}

function ValoracionMinisterio({ positionId }) {
  const { state, dispatch } = useStore();
  const [categoriaActiva, setCategoriaActiva] = useState(CATEGORIAS_MINISTERIO[0].id);
  const [verInforme, setVerInforme] = useState(false);
  const pos = state.positions.find(p => p.id === positionId);

  if (!pos) {
    return (
      <Card>
        <div className="empty">
          Puesto no encontrado. <button className="btn btn-ghost btn-sm" onClick={() => go("/valuation")}>Volver</button>
        </div>
      </Card>
    );
  }

  const { otros } = state.ministerio;
  const respuestas = state.ministerio.respuestas[pos.id] || {};
  const aplicables = subfactoresAplicables(otros);
  const { total, done } = progresoMinisterio(respuestas, otros);
  const progreso = total ? Math.round(done / total * 100) : 0;
  const porCategoria = puntosPorCategoria(respuestas, otros);
  const clasificacion = clasificacionDePuntos(pos.points, state);
  const dept = state.departments.find(d => d.id === pos.departmentId);
  const lvl = state.levels.find(l => l.id === pos.levelId);

  const indice = CATEGORIAS_MINISTERIO.findIndex(c => c.id === categoriaActiva);
  const categoria = CATEGORIAS_MINISTERIO[indice];
  const esUltima = indice === CATEGORIAS_MINISTERIO.length - 1;
  const responder = (codigo, respuesta) =>
    dispatch({ type: "SET_RESPUESTA_MINISTERIO", positionId: pos.id, codigo, respuesta });

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="row" style={{ gap: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => go("/valuation")}>
          <Icon name="chevron-left" size={14} /> Volver a la lista
        </button>
      </div>

      <Card>
        <div className="row" style={{ gap: 10, marginBottom: 8 }}>
          <Badge tone="brand">{pos.code}</Badge>
          <Badge>{dept?.name}</Badge>
          <Badge>{lvl?.name}</Badge>
          <Badge>Modelo Ministerio</Badge>
        </div>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, letterSpacing: "-0.025em" }}>
          Valorando: {pos.name}
        </h1>
        <div className="muted" style={{ fontSize: 13.5, marginTop: 6 }}>
          {done} de {total} subfactores valorados · {progreso}% completado
        </div>
        <div className="bar" style={{ marginTop: 14, maxWidth: 420 }}>
          <div className="bar-fill" style={{ width: progreso + "%" }} />
        </div>
      </Card>

      <div className="grid" style={{ gridTemplateColumns: "1fr 280px", alignItems: "start" }}>
        <div className="stack" style={{ gap: 14 }}>
          <Segmented
            value={categoriaActiva}
            onChange={setCategoriaActiva}
            options={CATEGORIAS_MINISTERIO.map(c => ({ value: c.id, label: `${c.id}. ${c.corto}` }))}
          />
          <Card eyebrow={`Categoría ${categoria.id}`} title={categoria.nombre}>
            <div className="stack" style={{ gap: 18 }}>
              {SUBGRUPOS_MINISTERIO.filter(g => g.categoria === categoria.id).map(g => {
                const delGrupo = aplicables.filter(sf => sf.subgrupo === g.id);
                if (delGrupo.length === 0) return null;
                return (
                  <div key={g.id}>
                    <div className="card-eyebrow" style={{ marginBottom: 8 }}>{g.nombre}</div>
                    <div className="stack" style={{ gap: 10 }}>
                      {delGrupo.map(sf => (
                        <SubfactorPregunta
                          key={sf.codigo}
                          sf={sf}
                          textos={textosDe(sf, otros)}
                          respuesta={respuestas[sf.codigo]}
                          onElegir={(r) => responder(sf.codigo, r)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
          <div className="spread">
            <Button variant="ghost" disabled={indice === 0}
              onClick={() => setCategoriaActiva(CATEGORIAS_MINISTERIO[indice - 1].id)}>
              <Icon name="chevron-left" size={14} /> Anterior
            </Button>
            {esUltima ? (
              <Button variant="primary" onClick={() => setVerInforme(true)}>Ver informe</Button>
            ) : (
              <Button variant="primary" onClick={() => setCategoriaActiva(CATEGORIAS_MINISTERIO[indice + 1].id)}>
                Siguiente categoría <Icon name="chevron-right" size={14} />
              </Button>
            )}
          </div>
        </div>

        <Card title="Resultado" style={{ position: "sticky", top: 16 }}>
          <div className="stack" style={{ gap: 8 }}>
            {CATEGORIAS_MINISTERIO.map(c => (
              <div key={c.id} className="spread">
                <span style={{ fontSize: 13 }}>{c.id}. {c.corto}</span>
                <span className="mono semi">{Math.round(porCategoria[c.id])}</span>
              </div>
            ))}
            <div className="spread" style={{ paddingTop: 8, borderTop: "1px solid var(--border)" }}>
              <span className="semi">Total</span>
              <span className="display-num" style={{ fontSize: 28 }} data-testid="total-ministerio">
                {Math.round(pos.points)}
              </span>
            </div>
            {clasificacion
              ? <ClasificacionBadge clasificacion={clasificacion} showLabel />
              : <span className="tiny muted">Sin agrupación aún</span>}
            {clasificacion && pos.status !== "FINALIZADA" && (
              <div className="tiny muted">Previsualización: no es definitiva hasta finalizar la valoración.</div>
            )}
            <Button onClick={() => setVerInforme(true)} style={{ marginTop: 6 }}>Ver informe</Button>
          </div>
        </Card>
      </div>

      <InformeMinisterio
        open={verInforme}
        onClose={() => setVerInforme(false)}
        position={pos}
        respuestas={respuestas}
        aplicables={aplicables}
        otros={otros}
        clasificacion={clasificacion}
      />
    </div>
  );
}

function SubfactorPregunta({ sf, textos, respuesta, onElegir }) {
  const nivel = nivelDeRespuesta(sf, respuesta);
  const titulo = sf.otros ? textos.nombre.replace(`${sf.codigo}) `, "") : sf.nombre;
  return (
    <div role="group" aria-label={`${sf.codigo} ${sf.otros ? "Otros" : sf.nombre}`}
         className="surface-soft" style={{ padding: 14 }}>
      <div className="row" style={{ gap: 10, alignItems: "flex-start", marginBottom: 8 }}>
        <span className="mono semi" style={{ fontSize: 12, minWidth: 44 }}>{sf.codigo}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="semi" style={{ fontSize: 14 }}>{titulo}</div>
          {textos.definicion && (
            <div className="muted" style={{ fontSize: 12.5, marginTop: 4, textWrap: "pretty" }}>{textos.definicion}</div>
          )}
        </div>
        {nivel != null
          ? <Badge tone="good">{conDosDecimales(puntosDeSubfactor(sf, respuesta))} pts</Badge>
          : <Badge>Sin responder</Badge>}
      </div>
      {sf.tipo === "matriz"
        ? <Matriz sf={sf} respuesta={respuesta} nivel={nivel} onElegir={onElegir} />
        : <Niveles niveles={textos.niveles.slice(0, sf.opciones + 1)} respuesta={respuesta} onElegir={onElegir} />}
    </div>
  );
}

function Niveles({ niveles, respuesta, onElegir }) {
  return (
    <div className="stack" style={{ gap: 2 }}>
      {niveles.map((texto, k) => {
        const on = respuesta === k;
        return (
          <button key={k} type="button" aria-pressed={on} onClick={() => onElegir(k)}
            style={{
              width: "100%", textAlign: "left", border: "none", borderRadius: 10,
              background: on ? "var(--bg-soft)" : "transparent",
              padding: "8px 12px", display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer",
            }}>
            <span className={"radio " + (on ? "on" : "")} />
            <span style={{ fontSize: 13 }}>{texto}</span>
          </button>
        );
      })}
    </div>
  );
}

/* Matriz oficial: nivel = eje1 + eje2 − 1 (1 2 3 / 2 3 4 / 3 4 5). */
function Matriz({ sf, respuesta, nivel, onElegir }) {
  const ejes = EJES_MINISTERIO[sf.ejes];
  return (
    <div className="stack" style={{ gap: 8 }}>
      <table className="t-table">
        <thead>
          <tr>
            <th>{ejes.eje1.nombre} / {ejes.eje2.nombre}</th>
            {ejes.eje2.valores.map(v2 => <th key={v2}>{v2}</th>)}
          </tr>
        </thead>
        <tbody>
          {ejes.eje1.valores.map((v1, i) => (
            <tr key={v1}>
              <th scope="row" style={{ fontWeight: 500, fontSize: 12.5 }}>{v1}</th>
              {ejes.eje2.valores.map((v2, j) => {
                const on = respuesta?.eje1 === i + 1 && respuesta?.eje2 === j + 1;
                return (
                  <td key={v2}>
                    <button type="button" aria-pressed={on} aria-label={`Nivel ${i + j + 1}: ${v1} · ${v2}`}
                      className={"btn btn-sm " + (on ? "btn-primary" : "btn-secondary")}
                      onClick={() => onElegir({ eje1: i + 1, eje2: j + 1 })}>
                      {i + j + 1}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="row" style={{ gap: 12 }}>
        <button type="button" aria-pressed={respuesta === 0}
          className={"btn btn-sm " + (respuesta === 0 ? "btn-primary" : "btn-secondary")}
          onClick={() => onElegir(0)}>
          No relevante
        </button>
        <span className="tiny muted">Nivel resultante: {nivel ?? "—"}</span>
      </div>
    </div>
  );
}

/* Informe del puesto: cada subfactor con su nivel y sus puntos con dos
   decimales, subtotal por categoría y total con la agrupación. */
function InformeMinisterio({ open, onClose, position, respuestas, aplicables, otros, clasificacion }) {
  const porCategoria = puntosPorCategoria(respuestas, otros);
  return (
    <Modal open={open} onClose={onClose} title={`Informe · ${position.name}`} width={760}
      footer={<Button variant="primary" onClick={onClose}>Cerrar</Button>}>
      <table className="t-table">
        <thead>
          <tr><th>Código</th><th>Subfactor</th><th>Nivel</th><th style={{ textAlign: "right" }}>Puntos</th></tr>
        </thead>
        <tbody>
          {CATEGORIAS_MINISTERIO.map(c => (
            <React.Fragment key={c.id}>
              {aplicables.filter(sf => sf.categoria === c.id).map(sf => {
                const nivel = nivelDeRespuesta(sf, respuestas[sf.codigo]);
                return (
                  <tr key={sf.codigo}>
                    <td className="mono">{sf.codigo}</td>
                    <td>{sf.otros ? otros[sf.codigo].nombre : sf.nombre}</td>
                    <td className="mono">{nivel ?? "—"}</td>
                    <td className="mono" style={{ textAlign: "right" }}>{conDosDecimales(puntosDeSubfactor(sf, respuestas[sf.codigo]))}</td>
                  </tr>
                );
              })}
              <tr>
                <td colSpan="3" className="semi">Subtotal {c.id}. {c.corto}</td>
                <td className="mono semi" style={{ textAlign: "right" }}>{conDosDecimales(porCategoria[c.id])}</td>
              </tr>
            </React.Fragment>
          ))}
          <tr>
            <td colSpan="3" className="bold">
              {clasificacion && position.status === "FINALIZADA" ? `Total · ${clasificacion.etiqueta}` : "Total"}
            </td>
            <td className="mono bold" style={{ textAlign: "right" }}>{conDosDecimales(position.points)}</td>
          </tr>
        </tbody>
      </table>
    </Modal>
  );
}

export { ValoracionMinisterio };
